/**
 * AutoLISP interpreter core (P2). Async evaluator: every eval step can
 * suspend (get* primitives await UI interaction) and yields to the event
 * loop periodically so heavy loops never freeze the UI. AutoLISP semantics:
 * dynamic scoping, nil/T atoms, case-insensitive symbols, integer division.
 */

import { LispError } from './errors';
import {
    Env,
    NIL,
    T,
    mkList,
    mkStr,
    mkSym,
    numVal,
    truthy,
    symName,
    type LispValue,
} from './values';
import type { LispNode } from './parser';
import { parseProgram } from './parser';
import { registerStdlib } from './stdlib';
import { registerLispCommand } from './registry';

interface ConsNode {
    t: 'cons';
    items: LispNode[];
    dot?: LispNode | null;
}

function requireNode(node: LispNode | undefined): LispNode {
    if (!node) throw new LispError('too few arguments');
    return node;
}

function requireCons(node: LispNode | undefined): ConsNode {
    const n = requireNode(node);
    if (n.t !== 'cons') throw new LispError('syntax error');
    return n;
}

/** Evaluate a quoted AST into literal values. */
function quoteAst(node: LispNode): LispValue {
    switch (node.t) {
        case 'num':
            return node.isInt ? { k: 'int', v: node.v } : { k: 'real', v: node.v };
        case 'str':
            return mkStr(node.v);
        case 'sym':
            if (node.name === 'T') return T;
            if (node.name === 'NIL') return NIL;
            return mkSym(node.name);
        case 'quote':
            return quoteAst(node.node);
        case 'cons':
            return mkList(
                node.items.map(quoteAst),
                node.dot ? quoteAst(node.dot) : null
            );
    }
}

/** Convert a runtime value back into evaluable AST (the `eval` subr). */
export function valueToAst(v: LispValue): LispNode {
    switch (v.k) {
        case 'int':
        case 'real':
            return { t: 'num', v: v.v, isInt: v.k === 'int' };
        case 'str':
            return { t: 'str', v: v.v };
        case 'sym':
            return { t: 'sym', name: v.name };
        case 't':
            return { t: 'sym', name: 'T' };
        case 'nil':
            return { t: 'sym', name: 'NIL' };
        case 'list':
            return {
                t: 'cons',
                items: v.items.map(valueToAst),
                dot: v.dot ? valueToAst(v.dot) : null,
            };
        default:
            throw new LispError('bad argument type');
    }
}

function describeFunction(fn: LispValue): string {
    if (fn.k === 'sym') return fn.name;
    if (fn.k === 'list') return 'LIST';
    return fn.k.toUpperCase();
}

// ---------------------------------------------------------------------------
// Special forms (unevaluated argument positions)
// ---------------------------------------------------------------------------

type SpecialFn = (node: ConsNode, env: Env, rt: LispRuntime) => Promise<LispValue>;

const SPECIAL_FORMS: Record<string, SpecialFn> = {
    QUOTE: async (node) => quoteAst(requireNode(node.items[1])),

    SETQ: async (node, env, rt) => {
        if ((node.items.length - 1) % 2 !== 0) throw new LispError('syntax error');
        let last: LispValue = NIL;
        for (let i = 1; i < node.items.length; i += 2) {
            const symNode = requireNode(node.items[i]);
            if (symNode.t !== 'sym') throw new LispError('syntax error');
            last = await rt.eval(requireNode(node.items[i + 1]), env);
            if (!env.assign(symNode.name, last)) rt.globalEnv.define(symNode.name, last);
        }
        return last;
    },

    SET: async (node, env, rt) => {
        const target = await rt.eval(requireNode(node.items[1]), env);
        const name = symName(target);
        if (!name) throw new LispError('bad argument type');
        const value = await rt.eval(requireNode(node.items[2]), env);
        if (!env.assign(name, value)) rt.globalEnv.define(name, value);
        return value;
    },

    DEFUN: async (node, _env, rt) => {
        const nameNode = requireNode(node.items[1]);
        if (nameNode.t !== 'sym') throw new LispError('syntax error');
        const paramsNode = requireCons(node.items[2]);
        const params: string[] = [];
        if (paramsNode.dot) throw new LispError('syntax error');
        for (const p of paramsNode.items) {
            if (p.t !== 'sym') throw new LispError('syntax error');
            params.push(p.name);
        }
        const fn: LispValue = {
            k: 'usubr',
            name: nameNode.name,
            params,
            body: node.items.slice(3),
        };
        rt.globalEnv.define(nameNode.name, fn);
        if (nameNode.name.startsWith('C:')) {
            registerLispCommand(nameNode.name.slice(2), fn, rt.commandSource);
        }
        return mkSym(nameNode.name);
    },

    LAMBDA: async (node) => {
        const paramsNode = requireCons(node.items[1]);
        const params: string[] = [];
        if (paramsNode.dot) throw new LispError('syntax error');
        for (const p of paramsNode.items) {
            if (p.t !== 'sym') throw new LispError('syntax error');
            params.push(p.name);
        }
        const fn: LispValue = { k: 'usubr', name: 'LAMBDA', params, body: node.items.slice(2) };
        return fn;
    },

    IF: async (node, env, rt) => {
        const test = await rt.eval(requireNode(node.items[1]), env);
        if (truthy(test)) return rt.eval(requireNode(node.items[2]), env);
        if (node.items[3]) return rt.eval(node.items[3], env);
        return NIL;
    },

    COND: async (node, env, rt) => {
        for (let i = 1; i < node.items.length; i++) {
            const clause = requireCons(node.items[i]);
            const test = await rt.eval(requireNode(clause.items[0]), env);
            if (truthy(test)) {
                if (clause.items.length === 1) return test;
                let last: LispValue = NIL;
                for (let b = 1; b < clause.items.length; b++) {
                    last = await rt.eval(clause.items[b], env);
                }
                return last;
            }
        }
        return NIL;
    },

    WHILE: async (node, env, rt) => {
        let last: LispValue = NIL;
        for (;;) {
            const test = await rt.eval(requireNode(node.items[1]), env);
            if (!truthy(test)) return last;
            for (let i = 2; i < node.items.length; i++) {
                last = await rt.eval(node.items[i], env);
            }
        }
    },

    REPEAT: async (node, env, rt) => {
        const count = numVal(await rt.eval(requireNode(node.items[1]), env));
        let last: LispValue = NIL;
        for (let i = 0; i < Math.trunc(count); i++) {
            for (let b = 2; b < node.items.length; b++) {
                last = await rt.eval(node.items[b], env);
            }
        }
        return last;
    },

    FOREACH: async (node, env, rt) => {
        const nameNode = requireNode(node.items[1]);
        if (nameNode.t !== 'sym') throw new LispError('syntax error');
        const listVal = await rt.eval(requireNode(node.items[2]), env);
        if (listVal.k !== 'list' && listVal.k !== 'nil') throw new LispError('bad argument type');
        let last: LispValue = NIL;
        if (listVal.k === 'list') {
            // AutoLISP foreach binds the variable dynamically and leaves the
            // last value behind after the loop.
            for (const item of listVal.items) {
                env.define(nameNode.name, item);
                for (let b = 3; b < node.items.length; b++) {
                    last = await rt.eval(node.items[b], env);
                }
            }
        }
        return last;
    },

    AND: async (node, env, rt) => {
        for (let i = 1; i < node.items.length; i++) {
            const v = await rt.eval(node.items[i], env);
            if (!truthy(v)) return NIL;
        }
        return T;
    },

    OR: async (node, env, rt) => {
        for (let i = 1; i < node.items.length; i++) {
            const v = await rt.eval(node.items[i], env);
            if (truthy(v)) return v;
        }
        return NIL;
    },
};

export interface LispRuntimeOptions {
    /** Maximum eval steps per run before `; error: step budget exceeded`. */
    stepBudget?: number;
    /** Event-loop yield interval (steps) — keeps `while` loops responsive. */
    yieldEvery?: number;
}

export class LispRuntime {
    private global: Env;
    private steps = 0;
    private running = 0;
    private cancelled = false;
    private readonly stepBudget: number;
    private readonly yieldEvery: number;
    /** Transcript sink (command line / F2 window); bound by the app bootstrap. */
    out: (text: string) => void = () => {};
    /** Source tag attributed to c: commands defined during the current load. */
    commandSource: string | null = null;

    constructor(options: LispRuntimeOptions = {}) {
        this.stepBudget = options.stepBudget ?? 2_000_000;
        this.yieldEvery = options.yieldEvery ?? 2_000;
        this.global = new Env(null);
        registerStdlib(this);
    }

    get globalEnv(): Env {
        return this.global;
    }

    /** True while a top-level evaluation is in flight. */
    get busy(): boolean {
        return this.running > 0;
    }

    /** Drop user state; built-ins are reinstalled, c: registry is untouched. */
    reset(): void {
        if (this.running > 0) throw new LispError('cannot reset while running');
        this.global = new Env(null);
        this.steps = 0;
        this.cancelled = false;
        registerStdlib(this);
    }

    /** Request cancellation — takes effect at the next yield point. */
    cancel(): void {
        this.cancelled = true;
    }

    /** Cancellation flag poll (used by bridge loops between yield points). */
    isCancelled(): boolean {
        return this.cancelled;
    }

    /** Clear a stale cancel flag at the start of a new run. */
    clearCancel(): void {
        this.cancelled = false;
    }

    private async tick(): Promise<void> {
        this.steps++;
        if (this.steps > this.stepBudget) {
            throw new LispError('step budget exceeded');
        }
        if (this.steps % this.yieldEvery === 0) {
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
            if (this.cancelled) throw new LispError('Function cancelled');
        }
    }

    /** Run a source string with top-level semantics; returns the last value. */
    async evalString(src: string): Promise<LispValue> {
        const forms = parseProgram(src);
        this.clearCancel();
        this.running++;
        try {
            let last: LispValue = NIL;
            for (const form of forms) last = await this.eval(form);
            return last;
        } finally {
            this.running--;
        }
    }

    /** Evaluate one AST node (the runtime-API entry for primitive functions). */
    async evalNode(node: LispNode, env: Env = this.global): Promise<LispValue> {
        return this.eval(node, env);
    }

    async eval(node: LispNode, env: Env = this.global): Promise<LispValue> {
        await this.tick();
        switch (node.t) {
            case 'num':
                return node.isInt ? { k: 'int', v: node.v } : { k: 'real', v: node.v };
            case 'str':
                return mkStr(node.v);
            case 'sym': {
                if (node.name === 'T') return T;
                if (node.name === 'NIL') return NIL;
                return env.lookup(node.name);
            }
            case 'quote':
                return quoteAst(node.node);
            case 'cons': {
                if (node.items.length === 0) return NIL; // () is nil
                const head = node.items[0];
                if (head?.t === 'sym') {
                    const special = SPECIAL_FORMS[head.name];
                    if (special) return special(node, env, this);
                }
                const fn = await this.eval(requireNode(head), env);
                const args: LispValue[] = [];
                for (let i = 1; i < node.items.length; i++) {
                    args.push(await this.eval(node.items[i], env));
                }
                if (node.dot) args.push(await this.eval(node.dot, env));
                return this.apply(fn, args);
            }
        }
    }

    /** Apply a function value to already-evaluated arguments. */
    async apply(fn: LispValue, args: LispValue[]): Promise<LispValue> {
        await this.tick();
        if (fn.k === 'subr') return fn.fn(args, this);
        if (fn.k === 'usubr') {
            // Dynamic scoping: parameters bind in a child of the GLOBAL env,
            // so free symbols resolve against globals and caller bindings —
            // faithful to AutoLISP.
            if (args.length < fn.params.length) throw new LispError('too few arguments');
            if (args.length > fn.params.length) throw new LispError('too many arguments');
            const callEnv = new Env(this.global);
            fn.params.forEach((p, i) => callEnv.define(p, args[i]));
            let last: LispValue = NIL;
            for (const bodyNode of fn.body) last = await this.eval(bodyNode, callEnv);
            return last;
        }
        throw new LispError(`no function definition: ${describeFunction(fn)}`);
    }

    /** The `eval` subr implementation: value → AST → evaluate. */
    async evalValue(v: LispValue): Promise<LispValue> {
        return this.eval(valueToAst(v));
    }
}

export { quoteAst };
